import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import { fetchSupervisors } from "actions/employeeActions";

import {
	fetchJobPostings,
	clearJobPostings,
	deleteJobPosting
} from "api/menu/hr/job-postings/actions";

import { closeCustomModal, openCustomModal } from "components/custom/modals";

const JobPostingModal = React.lazy(() => import("./job-posting-modal"));

class JobPostingsList extends Component {
	constructor(props) {
		super(props);
        this.state = {
            includeInactive: false
        };
	}

	componentDidMount() {
		this.props.fetchSupervisors();
		this.props.fetchJobPostings(this.state.includeInactive);
	}

	componentWillUnmount() {
		this.props.clearJobPostings();
	}

	openModal = (id = false) => {
		const modalTitle = id !== false ? "Update" : "Create";
		const modalObj = {
			id: `job-posting-${id}`,
			header: `${modalTitle} Job Posting`,
			modalSettings: { size: "lg" },
			children: (
				<Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
					<JobPostingModal
						jobPostingId={id}
						onCancel={() => this.props.closeCustomModal(modalObj)}
						onSaved={() => {
							this.props.fetchJobPostings(this.state.includeInactive);
							this.props.closeCustomModal(modalObj);
						}}
					/>
				</Suspense>
			),
			handleClose: () => this.props.closeCustomModal(modalObj)
		};
		this.props.openCustomModal(modalObj);
	};

	delete = (id, title) => {
		this.props.deleteJobPosting(id, title, () => this.props.fetchJobPostings(this.state.includeInactive));
	};

	render() {
		const { jobPostings, loading } = this.props;
		const columns = [
			{
				Header: "Title",
				accessor: "title",
				id: "title",
				Cell: ({ original }) => (
					<span>
						{original.title}
						{!original.jobDescription && (
							<FontAwesomeIcon
								icon="exclamation"
								className="text-danger ml-2"
								title="Needs attention: no job description set. AI scoring and interview features are disabled for its applicants."
							/>
						)}
					</span>
				)
			},
			{ Header: "City", accessor: "cityName", id: "cityName" },
			{ Header: "Province", accessor: "provinceName", id: "provinceName" },
			{ Header: "HR Representative", accessor: "hrName", id: "hrName" },
			{ Header: "Supervisor", accessor: "supervisorName", id: "supervisorName" },
			{ 
				Header: "Applicants", 
				accessor: "applicantCount", 
				id: "applicantCount",
				className: "d-flex justify-content-center align-items-center",
				width: 100
			},
			{
                Header: "Active",
				id: "isActive",
				accessor: original => (original.isActive ? "Yes" : "No"),
				Cell: ({ original }) => (original.isActive ? "Yes" : "No"),
				className: "d-flex justify-content-center align-items-center",
				Filter: ({ filter, onChange }) => (
					<ReactTableFilterSelect
						currentValue={filter ? filter.value : ""}
						onChange={value => onChange(value)}
						style={{ width: "100%", height: "100%" }}
						optionsObject={{ "": "Show All", YES: "Yes", NO: "No" }}
					/>
				)
			},
			{
				Header: "Actions",
				id: "actions",
				className: "d-flex justify-content-center align-items-center",
				Cell: ({ original }) => (
					<ButtonGroup>
						<Button size="sm" onClick={() => this.openModal(original.id)}>
							<FontAwesomeIcon icon="pen" title="Update" />
						</Button>
						<Popconfirm
							title={`Delete "${original.title}"?`}
							onConfirm={() => this.delete(original.id, original.title)}
							placement="bottomRight"
							okText="Yes"
							cancelText="Cancel"
						>
							<Button variant="danger" size="sm">
								<FontAwesomeIcon icon="times" />
							</Button>
						</Popconfirm>
					</ButtonGroup>
				),
				filterable: false,
				width: 100
			}
		];

		return (
            <div className="dashboard">
                <h3 className="mb-4 d-flex align-items-center">Job Postings
                    <Button className="ml-auto" variant="primary" onClick={() => this.openModal()}>
                        Add Job Posting
                    </Button>
                </h3>

				<ReactTable
					data={jobPostings}
					headerClassName="table-header"
					columns={columns}
					defaultPageSize={20}
					pageSizeOptions={[10, 20, 50, 100]}
					resizable
					className="-striped -highlight"
					loading={loading}
					filterable
					defaultFilterMethod={filterCaseInsensitive}
					defaultSorted={[{ id: "title", desc: false }]}
					getNoDataProps={() => ({ style: { zIndex: 0 } })}
					style={{ height: "calc(100vh - 140px)" }}
				/>
			</div>
		);
	}
}

JobPostingsList.propTypes = {
	fetchJobPostings: PropTypes.func.isRequired,
	clearJobPostings: PropTypes.func.isRequired,
	deleteJobPosting: PropTypes.func.isRequired,
	fetchSupervisors: PropTypes.func.isRequired,
	jobPostings: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
	jobPostings: state.jobPostings.jobPostings,
	loading: state.jobPostings.loading
});

const mapDispatchToProps = dispatch => ({
	fetchJobPostings: bindActionCreators(fetchJobPostings, dispatch),
	clearJobPostings: bindActionCreators(clearJobPostings, dispatch),
	deleteJobPosting: bindActionCreators(deleteJobPosting, dispatch),
	openCustomModal: bindActionCreators(openCustomModal, dispatch),
	closeCustomModal: bindActionCreators(closeCustomModal, dispatch),
	fetchSupervisors: bindActionCreators(fetchSupervisors, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(JobPostingsList);


